/**
 * useSignalR Hook
 * Manages SignalR connection for real-time comment updates
 */
import { useEffect, useRef, useState } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import {
  createCommentsConnection,
  type CommentsConnection,
} from '@/services/signalr';
import { retryStart } from '@/services/signalr/retryPolicy';
import type { CommentResponseDto } from '@/types/api/responses';

interface UseSignalROptions {
  /** Task ID to subscribe to (null to disconnect) */
  taskId: string | null;
  /** Callback when a new comment is added */
  onCommentAdded?: (comment: CommentResponseDto) => void;
  /** Callback when a comment is updated */
  onCommentUpdated?: (comment: CommentResponseDto) => void;
  /** Callback when a comment is deleted */
  onCommentDeleted?: (commentId: string) => void;
}

interface UseSignalRReturn {
  /** Whether connection is established */
  isConnected: boolean;
  /** Whether connection is reconnecting */
  isReconnecting: boolean;
  /** Connection error message if any */
  connectionError: string | null;
}

/**
 * Hook for managing SignalR connection to comments hub
 *
 * @example
 * const { isConnected, isReconnecting, connectionError } = useSignalR({
 *   taskId: selectedTaskId,
 *   onCommentAdded: (comment) => addCommentToList(comment),
 *   onCommentUpdated: (comment) => updateCommentInList(comment),
 *   onCommentDeleted: (commentId) => removeCommentFromList(commentId),
 * });
 */
export function useSignalR(options: UseSignalROptions): UseSignalRReturn {
  const { taskId, onCommentAdded, onCommentUpdated, onCommentDeleted } =
    options;

  const connectionRef = useRef<CommentsConnection | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Store callbacks in refs to avoid recreating connection on callback changes
  const onCommentAddedRef = useRef(onCommentAdded);
  const onCommentUpdatedRef = useRef(onCommentUpdated);
  const onCommentDeletedRef = useRef(onCommentDeleted);

  // Update refs when callbacks change
  useEffect(() => {
    onCommentAddedRef.current = onCommentAdded;
  }, [onCommentAdded]);

  useEffect(() => {
    onCommentUpdatedRef.current = onCommentUpdated;
  }, [onCommentUpdated]);

  useEffect(() => {
    onCommentDeletedRef.current = onCommentDeleted;
  }, [onCommentDeleted]);

  // Setup connection and event handlers
  useEffect(() => {
    let aborted = false;
    const connection = createCommentsConnection();
    connectionRef.current = connection;

    // Register event handlers with stable wrapper functions
    connection.on('CommentAdded', (comment: CommentResponseDto) => {
      onCommentAddedRef.current?.(comment);
    });

    connection.on('CommentUpdated', (comment: CommentResponseDto) => {
      onCommentUpdatedRef.current?.(comment);
    });

    connection.on('CommentDeleted', (payload: { commentId: string }) => {
      onCommentDeletedRef.current?.(payload.commentId);
    });

    // Connection state handlers
    connection.onreconnecting(() => {
      setIsReconnecting(true);
      setIsConnected(false);
    });

    connection.onreconnected(() => {
      setIsReconnecting(false);
      setIsConnected(true);
      setConnectionError(null);

      // Rejoin current task room after reconnection
      if (currentTaskIdRef.current) {
        connection
          .invoke('JoinTask', currentTaskIdRef.current)
          .catch((error) =>
            console.error('Failed to rejoin task room:', error)
          );
      }
    });

    function handleConnected(): void {
      setIsConnected(true);
      setConnectionError(null);
      if (currentTaskIdRef.current) {
        connection
          .invoke('JoinTask', currentTaskIdRef.current)
          .catch((err) => console.error('Failed to rejoin task room:', err));
      }
    }

    function scheduleRetry(): void {
      retryStart(
        () => connection.start(),
        () => aborted,
        handleConnected
      );
    }

    connection.onclose((error) => {
      setIsConnected(false);
      setIsReconnecting(false);
      if (error) {
        setConnectionError(error.message);
      }
      if (!aborted) scheduleRetry();
    });

    // Start connection
    connection
      .start()
      .then(() => {
        setIsConnected(true);
        setConnectionError(null);
      })
      .catch((error) => {
        setConnectionError(error.message);
        scheduleRetry();
      });

    // Cleanup on unmount
    return () => {
      aborted = true;
      connection.stop();
    };
  }, []);

  // Join/Leave task room when taskId changes
  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || connection.state !== HubConnectionState.Connected) {
      return;
    }

    // Leave previous task room
    if (currentTaskIdRef.current && currentTaskIdRef.current !== taskId) {
      connection
        .invoke('LeaveTask', currentTaskIdRef.current)
        .catch((error) => console.error('Failed to leave task room:', error));
    }

    // Join new task room
    if (taskId) {
      connection
        .invoke('JoinTask', taskId)
        .catch((error) => console.error('Failed to join task room:', error));
    }

    currentTaskIdRef.current = taskId;
  }, [taskId, isConnected]);

  return { isConnected, isReconnecting, connectionError };
}
